import React, { useState, useEffect } from 'react';
import api from '../../services/api';
import '../../components/Layout/Layout.css';

const ALERT_TYPES = [
  { value:'price_above',   label:'📈 Price Above',        desc:'Alert when price rises above target' },
  { value:'price_below',   label:'📉 Price Below',        desc:'Alert when price falls below target' },
  { value:'stop_loss',     label:'🛑 Stop Loss',          desc:'Alert when stop loss is hit' },
  { value:'profit_target', label:'🎯 Profit Target',      desc:'Alert when profit target is reached' },
  { value:'index_move',    label:'📊 Index Movement',     desc:'Alert when index moves by X%' },
];

const MARKETS = [
  { value:'india',  label:'🇮🇳 India'  },
  { value:'us',     label:'🇺🇸 US'     },
  { value:'crypto', label:'₿ Crypto'   },
  { value:'global', label:'🌍 Global'  },
  { value:'index',  label:'📊 Index'   },
];

const TYPE_COLORS = {
  price_above:   '#00ff88',
  price_below:   '#ff4d4d',
  stop_loss:     '#ff6b35',
  profit_target: '#00b8ff',
  index_move:    '#ffb800',
  ai_signal:     '#a855f7',
};

export default function AlertsPage() {
  const [alerts,    setAlerts]    = useState([]);
  const [loading,   setLoading]   = useState(false);
  const [checking,  setChecking]  = useState(false);
  const [showCreate,setShowCreate]= useState(false);
  const [message,   setMessage]   = useState('');
  const [form, setForm] = useState({
    name:'', alert_type:'price_above', market:'india',
    symbol:'', condition:'above', target_value:'',
  });

  useEffect(() => { fetchAlerts(); }, []);

  const fetchAlerts = async () => {
    setLoading(true);
    try {
      const r = await api.get('/alerts');
      setAlerts(r.data);
    } catch(e) { console.error(e); }
    finally { setLoading(false); }
  };

  const createAlert = async (e) => {
    e.preventDefault();
    try {
      await api.post('/alerts', { ...form, target_value: parseFloat(form.target_value) });
      setShowCreate(false);
      setForm({ name:'', alert_type:'price_above', market:'india', symbol:'', condition:'above', target_value:'' });
      fetchAlerts();
      setMessage('✅ Alert created successfully!');
    } catch(e) { setMessage(`❌ ${e.response?.data?.detail || e.message}`); }
  };

  const checkAlerts = async () => {
    setChecking(true); setMessage('');
    try {
      const r = await api.post('/alerts/check');
      setMessage(`✅ Checked ${r.data.checked} alerts — ${r.data.triggered} triggered`);
      fetchAlerts();
    } catch(e) { setMessage(`❌ ${e.response?.data?.detail || e.message}`); }
    finally { setChecking(false); }
  };

  const markRead = async (id) => {
    await api.put(`/alerts/${id}/read`);
    fetchAlerts();
  };

  const deleteAlert = async (id) => {
    if (!window.confirm('Delete this alert?')) return;
    await api.delete(`/alerts/${id}`);
    fetchAlerts();
  };

  const resetAlert = async (id) => {
    await api.put(`/alerts/${id}/reset`);
    fetchAlerts();
  };

  const inp = { width:'100%', background:'rgba(255,255,255,0.05)', border:'1px solid rgba(255,255,255,0.1)', borderRadius:'8px', padding:'10px 14px', color:'#fff', fontSize:'14px', outline:'none', boxSizing:'border-box' };
  const lbl = { fontSize:'12px', color:'rgba(255,255,255,0.4)', display:'block', marginBottom:'6px' };
  const sel = { ...inp, background:'#1a1f2e' };

  const active    = alerts.filter(a => !a.is_triggered);
  const triggered = alerts.filter(a => a.is_triggered);
  const unread    = triggered.filter(a => !a.is_read).length;

  return (
    <div className="page">
      <div className="page-header" style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start' }}>
        <div>
          <div className="page-title">
            🔔 Alerts
            {unread > 0 && <span style={{ marginLeft:'12px', background:'#ff4d4d', color:'#fff', borderRadius:'20px', padding:'2px 10px', fontSize:'14px', fontWeight:'700' }}>{unread} new</span>}
          </div>
          <div className="page-subtitle">Set price targets and get notified when they're hit</div>
        </div>
        <div style={{ display:'flex', gap:'10px' }}>
          <button className="btn btn-outline" onClick={checkAlerts} disabled={checking}>
            {checking ? '🔄 Checking...' : '🔍 Check Now'}
          </button>
          <button className="btn btn-primary" onClick={() => setShowCreate(true)}>+ New Alert</button>
        </div>
      </div>

      {message && (
        <div style={{ padding:'12px 16px', borderRadius:'10px', marginBottom:'20px', fontSize:'14px',
          background: message.startsWith('✅')?'rgba(0,255,136,0.1)':'rgba(255,77,77,0.1)',
          border:`1px solid ${message.startsWith('✅')?'rgba(0,255,136,0.3)':'rgba(255,77,77,0.3)'}`,
          color: message.startsWith('✅')?'#00ff88':'#ff4d4d',
        }}>{message}</div>
      )}

      {loading && <div className="loading"><div className="spinner"></div></div>}

      {/* Triggered Alerts */}
      {triggered.length > 0 && (
        <div style={{ marginBottom:'24px' }}>
          <div style={{ fontFamily:'Syne,sans-serif', fontWeight:'700', fontSize:'16px', marginBottom:'12px', color:'#ffb800' }}>
            🔥 Triggered Alerts ({triggered.length})
          </div>
          <div style={{ display:'flex', flexDirection:'column', gap:'10px' }}>
            {triggered.map(a => (
              <div key={a.id} style={{ padding:'16px', borderRadius:'12px', border:`1px solid ${a.is_read?'rgba(255,255,255,0.08)':'rgba(255,184,0,0.4)'}`, background: a.is_read?'rgba(255,255,255,0.02)':'rgba(255,184,0,0.05)', display:'flex', alignItems:'center', gap:'16px' }}>
                <div style={{ fontSize:'24px' }}>{a.is_read ? '✅' : '🔔'}</div>
                <div style={{ flex:1 }}>
                  <div style={{ fontWeight:'700', marginBottom:'4px' }}>{a.name}</div>
                  <div style={{ fontSize:'13px', color:'rgba(255,255,255,0.6)' }}>{a.message}</div>
                  {a.triggered_at && <div style={{ fontSize:'11px', color:'rgba(255,255,255,0.3)', marginTop:'4px' }}>{new Date(a.triggered_at).toLocaleString()}</div>}
                </div>
                <div style={{ display:'flex', gap:'8px' }}>
                  {!a.is_read && <button onClick={() => markRead(a.id)} style={{ padding:'6px 12px', borderRadius:'8px', border:'1px solid rgba(0,255,136,0.3)', background:'transparent', color:'#00ff88', cursor:'pointer', fontSize:'12px' }}>Mark Read</button>}
                  <button onClick={() => resetAlert(a.id)} style={{ padding:'6px 12px', borderRadius:'8px', border:'1px solid rgba(255,255,255,0.1)', background:'transparent', color:'rgba(255,255,255,0.5)', cursor:'pointer', fontSize:'12px' }}>Reset</button>
                  <button onClick={() => deleteAlert(a.id)} style={{ padding:'6px 12px', borderRadius:'8px', border:'1px solid rgba(255,77,77,0.3)', background:'transparent', color:'#ff4d4d', cursor:'pointer', fontSize:'12px' }}>Delete</button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Active Alerts */}
      <div>
        <div style={{ fontFamily:'Syne,sans-serif', fontWeight:'700', fontSize:'16px', marginBottom:'12px' }}>
          ⏳ Active Alerts ({active.length})
        </div>
        {active.length === 0 ? (
          <div className="card" style={{ textAlign:'center', padding:'60px' }}>
            <div style={{ fontSize:'48px', marginBottom:'16px' }}>🔔</div>
            <div style={{ color:'rgba(255,255,255,0.4)', marginBottom:'16px' }}>No active alerts</div>
            <button className="btn btn-primary" onClick={() => setShowCreate(true)}>Create Your First Alert</button>
          </div>
        ) : (
          <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill, minmax(340px,1fr))', gap:'14px' }}>
            {active.map(a => {
              const color = TYPE_COLORS[a.alert_type] || '#fff';
              const typeLabel = ALERT_TYPES.find(t => t.value === a.alert_type)?.label || a.alert_type;
              const progress = a.current_value && a.target_value
                ? Math.min(100, (parseFloat(a.current_value) / parseFloat(a.target_value)) * 100)
                : 0;
              return (
                <div key={a.id} style={{ padding:'16px', borderRadius:'12px', background:'rgba(255,255,255,0.03)', border:`1px solid ${color}25` }}>
                  <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom:'12px' }}>
                    <div>
                      <div style={{ fontWeight:'700', fontSize:'15px', marginBottom:'4px' }}>{a.name}</div>
                      <span style={{ padding:'3px 10px', borderRadius:'20px', fontSize:'11px', fontWeight:'600', background:`${color}15`, color }}>{typeLabel}</span>
                    </div>
                    <div style={{ textAlign:'right' }}>
                      <div style={{ fontFamily:'Syne,sans-serif', fontWeight:'800', fontSize:'16px' }}>{a.symbol}</div>
                      <div style={{ fontSize:'11px', color:'rgba(255,255,255,0.3)' }}>{a.market}</div>
                    </div>
                  </div>
                  <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'8px', marginBottom:'12px' }}>
                    <div style={{ padding:'8px', background:'rgba(255,255,255,0.03)', borderRadius:'7px' }}>
                      <div style={{ fontSize:'10px', color:'rgba(255,255,255,0.3)', marginBottom:'2px' }}>Target</div>
                      <div style={{ fontSize:'14px', fontWeight:'700', color }}>{a.target_value ? parseFloat(a.target_value).toLocaleString() : '--'}</div>
                    </div>
                    <div style={{ padding:'8px', background:'rgba(255,255,255,0.03)', borderRadius:'7px' }}>
                      <div style={{ fontSize:'10px', color:'rgba(255,255,255,0.3)', marginBottom:'2px' }}>Current</div>
                      <div style={{ fontSize:'14px', fontWeight:'700' }}>{a.current_value ? parseFloat(a.current_value).toLocaleString() : 'Checking...'}</div>
                    </div>
                  </div>
                  {a.current_value && a.target_value && (
                    <div style={{ marginBottom:'12px' }}>
                      <div style={{ background:'rgba(255,255,255,0.05)', borderRadius:'4px', height:'6px' }}>
                        <div style={{ height:'100%', borderRadius:'4px', background:color, width:`${progress}%`, transition:'width 0.5s' }}></div>
                      </div>
                      <div style={{ fontSize:'11px', color:'rgba(255,255,255,0.3)', marginTop:'3px' }}>{progress.toFixed(1)}% to target</div>
                    </div>
                  )}
                  <div style={{ display:'flex', gap:'6px' }}>
                    <button onClick={() => deleteAlert(a.id)} style={{ flex:1, padding:'6px', borderRadius:'7px', border:'1px solid rgba(255,77,77,0.3)', background:'transparent', color:'#ff4d4d', cursor:'pointer', fontSize:'12px' }}>🗑️ Delete</button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Create Alert Modal */}
      {showCreate && (
        <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.85)', display:'flex', alignItems:'center', justifyContent:'center', zIndex:1000, padding:'20px' }}>
          <div className="card" style={{ width:'480px', maxHeight:'90vh', overflowY:'auto' }}>
            <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:'24px' }}>
              <h3 style={{ fontFamily:'Syne,sans-serif', fontSize:'18px' }}>Create Alert</h3>
              <button onClick={() => setShowCreate(false)} style={{ background:'none', border:'none', color:'rgba(255,255,255,0.5)', cursor:'pointer', fontSize:'20px' }}>×</button>
            </div>
            <form onSubmit={createAlert} style={{ display:'flex', flexDirection:'column', gap:'14px' }}>
              <div><label style={lbl}>Alert Name *</label><input type="text" placeholder="e.g. RELIANCE target" value={form.name} onChange={e => setForm({...form,name:e.target.value})} required style={inp}/></div>
              <div>
                <label style={lbl}>Alert Type</label>
                <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'6px' }}>
                  {ALERT_TYPES.map(t => (
                    <button key={t.value} type="button" onClick={() => setForm({...form,alert_type:t.value})}
                      style={{ padding:'8px 10px', borderRadius:'8px', border:`1px solid ${form.alert_type===t.value?(TYPE_COLORS[t.value]||'#00ff88'):'rgba(255,255,255,0.1)'}`, background: form.alert_type===t.value?`${TYPE_COLORS[t.value]||'#00ff88'}15`:'transparent', color: form.alert_type===t.value?(TYPE_COLORS[t.value]||'#00ff88'):'rgba(255,255,255,0.5)', cursor:'pointer', fontSize:'12px', fontWeight:'600', textAlign:'left' }}>
                      {t.label}
                    </button>
                  ))}
                </div>
              </div>
              <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'10px' }}>
                <div><label style={lbl}>Market</label><select value={form.market} onChange={e => setForm({...form,market:e.target.value})} style={sel}>{MARKETS.map(m => <option key={m.value} value={m.value}>{m.label}</option>)}</select></div>
                <div><label style={lbl}>Symbol *</label><input type="text" placeholder="e.g. RELIANCE" value={form.symbol} onChange={e => setForm({...form,symbol:e.target.value.toUpperCase()})} required style={inp}/></div>
              </div>
              <div><label style={lbl}>Target Price / Value *</label><input type="number" placeholder="e.g. 1500" value={form.target_value} onChange={e => setForm({...form,target_value:e.target.value})} required style={inp}/></div>
              {form.alert_type === 'price_above' && <div style={{ padding:'10px', background:'rgba(0,255,136,0.05)', borderRadius:'8px', fontSize:'12px', color:'rgba(255,255,255,0.5)' }}>✅ You'll be alerted when {form.symbol||'the stock'} rises <strong>above</strong> {form.target_value||'your target'}</div>}
              {form.alert_type === 'price_below' && <div style={{ padding:'10px', background:'rgba(255,77,77,0.05)', borderRadius:'8px', fontSize:'12px', color:'rgba(255,255,255,0.5)' }}>⚠️ You'll be alerted when {form.symbol||'the stock'} falls <strong>below</strong> {form.target_value||'your target'}</div>}
              {form.alert_type === 'stop_loss' && <div style={{ padding:'10px', background:'rgba(255,107,53,0.05)', borderRadius:'8px', fontSize:'12px', color:'rgba(255,255,255,0.5)' }}>🛑 You'll be alerted when {form.symbol||'the stock'} hits your <strong>stop loss</strong> at {form.target_value||'your target'}</div>}
              <button type="submit" className="btn btn-primary">Create Alert</button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
